import { parseUptime } from '../config-columns'

export const createGraphTimeRangeParams = (preset, customRange, isCustom) => {
  if (isCustom && customRange.start && customRange.end) {
    return {
      start: Math.floor(customRange.start.getTime() / 1000),
      end: Math.floor(customRange.end.getTime() / 1000)
    }
  }
  return { preset }
}

export const getMetricValue = (equipment, key) => {
  if (equipment?.[key] != null) return equipment[key]
  return null
}

export const getFirstMetricValue = (equipment, keys) => {
  for (const key of keys) {
    const value = getMetricValue(equipment, key)
    if (value != null) return value
  }
  return null
}

export const formatDecimal = (value, digits = 0) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return '-'
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: digits }).format(number)
}

export const formatPercentValue = value => value == null ? null : formatDecimal(value)

export const formatUptimeValue = (uptime, t) => {
  if ([null, undefined, 0].includes(uptime)) return null
  const { nbDay, hour } = parseUptime(uptime)
  return nbDay ? t('supervision.tables.system_uptime_value', { count: nbDay, hour }) : hour
}

export const getApUptimeDays = equipment => {
  const uptime = getMetricValue(equipment, 'system_uptime')
  if (uptime == null) return null
  const value = Number(uptime)
  if (!Number.isFinite(value)) return null
  return value > 1000 ? value / 86400 : value
}

export const formatBytes = (bytes) => {
  const value = Number(bytes)
  if (!Number.isFinite(value)) return '-'
  if (value === 0) return '0'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const index = Math.min(Math.floor(Math.log(Math.abs(value)) / Math.log(1024)), units.length - 1)
  return `${formatDecimal(value / (1024 ** index), 2)} ${units[index]}`
}

export const getFirewallNode = (equipment, role, fallbackIndex) => {
  const nodes = equipment.cluster_stats?.nodes || []
  return nodes.find(node => node.name === role) || nodes[fallbackIndex] || null
}

export const getNumericTimestamp = timestamp => {
  if (timestamp == null || timestamp === '') return null
  const value = Number(timestamp)
  if (Number.isFinite(value)) return value < 10000000000 ? value * 1000 : value
  const date = new Date(timestamp).getTime()
  return Number.isFinite(date) ? date : null
}

export const formatGraphLabel = (timestamp, rangeMs) => {
  const normalizedTimestamp = getNumericTimestamp(timestamp)
  if (normalizedTimestamp == null) return ''
  const date = new Date(normalizedTimestamp)
  if (isNaN(date.getTime())) return ''
  if (rangeMs <= 2 * 24 * 60 * 60 * 1000) {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  }
  if (rangeMs <= 45 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }).replace('.', '')
  }
  return date.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '').toUpperCase()
}

export const buildLineData = ({ timestamps = [], values = [] }) => {
  const normalizedTimestamps = timestamps.map(getNumericTimestamp)
  const validTimestamps = normalizedTimestamps.filter(Number.isFinite)
  const rangeMs = validTimestamps.length > 1
    ? Math.max(...validTimestamps) - Math.min(...validTimestamps)
    : 0

  return timestamps.map((timestamp, index) => {
    const value = values[index]
    return {
      timestamp,
      timestampMs: normalizedTimestamps[index],
      label: formatGraphLabel(timestamp, rangeMs),
      value: Number(value) || 0
    }
  }).filter(({ timestampMs }) => Number.isFinite(timestampMs))
}

export const buildHistoryGraphData = history => buildLineData({
  timestamps: history?.timestamps || [],
  values: history?.status?.map(value => value == null ? null : value ? 100 : 0) || []
})

export const buildDiskUsageGraphData = graph => {
  const timestamps = graph?.timestamps || []
  const normalizedTimestamps = timestamps.map(getNumericTimestamp)
  const validTimestamps = normalizedTimestamps.filter(Number.isFinite)
  const rangeMs = validTimestamps.length > 1
    ? Math.max(...validTimestamps) - Math.min(...validTimestamps)
    : 0

  return timestamps.map((timestamp, index) => ({
    timestamp,
    timestampMs: normalizedTimestamps[index],
    label: formatGraphLabel(timestamp, rangeMs),
    disk_1_usage: Number(graph?.disk_1_usage?.[index]) || 0,
    disk_2_usage: Number(graph?.disk_2_usage?.[index]) || 0
  })).filter(({ timestampMs }) => Number.isFinite(timestampMs))
}

export const formatGraphTick = value => formatDecimal(value, value < 10 ? 1 : 0)

const SIX_HOURS_MS = 6 * 60 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

const getFirstAlignedTick = (timestamp, interval) => {
  const date = new Date(timestamp)
  date.setMinutes(0, 0, 0)

  if (interval === SIX_HOURS_MS) {
    const nextHour = Math.ceil(date.getHours() / 6) * 6
    date.setHours(nextHour)
    return date.getTime() < timestamp ? date.getTime() + SIX_HOURS_MS : date.getTime()
  }

  date.setHours(0)
  if (date.getTime() < timestamp) date.setDate(date.getDate() + 1)
  return date.getTime()
}

export const getGraphTimeTicks = (data = []) => {
  const timestamps = data.map(({ timestampMs }) => timestampMs).filter(Number.isFinite)
  if (timestamps.length < 2) return timestamps

  const min = Math.min(...timestamps)
  const max = Math.max(...timestamps)
  const range = max - min
  const interval = range <= 2 * DAY_MS ? SIX_HOURS_MS : range <= 8 * DAY_MS ? DAY_MS : 7 * DAY_MS
  const first = getFirstAlignedTick(min, interval)
  const ticks = []

  for (let timestamp = first; timestamp <= max; timestamp += interval) {
    ticks.push(timestamp)
  }

  return ticks.length ? ticks : [min, max]
}

export const formatGraphTimeTick = (timestamp, data = []) => {
  const timestamps = data.map(({ timestampMs }) => timestampMs).filter(Number.isFinite)
  const range = timestamps.length > 1 ? Math.max(...timestamps) - Math.min(...timestamps) : 0
  return formatGraphLabel(timestamp, range)
}

export const getMedianInterval = timestamps => {
  const intervals = timestamps
    .slice(1)
    .map((timestamp, index) => timestamp - timestamps[index])
    .filter(interval => interval > 0)
    .sort((a, b) => a - b)
  if (!intervals.length) return 1
  return intervals[Math.floor(intervals.length / 2)]
}

export const buildHistorySegments = (history) => {
  const statuses = history?.status || []
  const timestamps = (history?.timestamps || [])
    .map(getNumericTimestamp)
    .filter(timestamp => timestamp != null)

  if (!statuses.length) {
    return [{ key: 'empty', up: null, width: 100 }]
  }

  if (timestamps.length !== statuses.length || timestamps.length < 2) {
    const width = 100 / statuses.length
    return statuses.map((up, index) => ({ key: index, up, width }))
  }

  const expectedInterval = getMedianInterval(timestamps)
  const segments = []

  statuses.forEach((up, index) => {
    const nextTimestamp = timestamps[index + 1]
    const rawDuration = nextTimestamp ? nextTimestamp - timestamps[index] : expectedInterval
    const duration = rawDuration > 0 ? rawDuration : expectedInterval
    const hasGap = duration > expectedInterval * 1.8
    const activeDuration = hasGap ? expectedInterval : duration

    segments.push({ key: `${index}-status`, up, duration: activeDuration })

    if (hasGap) {
      segments.push({ key: `${index}-gap`, up: null, duration: duration - activeDuration })
    }
  })

  const totalDuration = segments.reduce((sum, segment) => sum + segment.duration, 0) || 1
  return segments.map(segment => ({
    ...segment,
    width: (segment.duration / totalDuration) * 100
  }))
}
